<template>
  <div class="metro-network-background" :class="{ 'visible': props.visible }">
    <canvas
      ref="canvasRef"
      :width="canvasWidth"
      :height="canvasHeight"
      class="metro-canvas"
      :class="{ 'visible': props.visible }"
      @mousedown="handleMouseDown"
      @mousemove="handleMouseMove"
      @mouseup="handleMouseUp"
      @wheel="handleWheel"
      @click="handleCanvasClick"
    />
    
    <!-- 上海地铁风格线路控制按钮 -->
    <div v-if="props.visible" class="metro-line-controls">
      <!-- 第一行：前一半线路 -->
      <div class="metro-line-row">
        <div 
          v-for="line in firstHalfLines" 
          :key="line.name"
          @click="toggleLineVisibility(line.name)"
          @mouseenter="handleLineButtonHover(line.name, $event)"
          @mouseleave="handleLineButtonLeave"
          class="shanghai-metro-line-btn"
          :class="{ 
            'active': lineVisibility[line.name],
            'inactive': !lineVisibility[line.name],
            'hovered': hoveredLine === line.name,
            'faded': hoveredLine && hoveredLine !== line.name,
            [`line-${line.name.replace(/号线|线/g, '').replace(/\s/g, '-')}`]: true
          }"
          :style="{ 
            '--line-color': line.color,
            '--text-color': getLineTextColor(line.color)
          }"
        >
          <!-- 线路信息 -->
          <div class="line-info">
            <div class="line-name-cn">{{ getLineDisplayName(line.name) }}</div>
            <div class="line-name-en">{{ getLineEnglishName(line.name) }}</div>
          </div>
        </div>
      </div>
      
      <!-- 第二行：后一半线路 -->
      <div class="metro-line-row">
        <div 
          v-for="line in secondHalfLines" 
          :key="line.name"
          @click="toggleLineVisibility(line.name)"
          @mouseenter="handleLineButtonHover(line.name, $event)"
          @mouseleave="handleLineButtonLeave"
          class="shanghai-metro-line-btn"
          :class="{ 
            'active': lineVisibility[line.name],
            'inactive': !lineVisibility[line.name],
            'hovered': hoveredLine === line.name,
            'faded': hoveredLine && hoveredLine !== line.name,
            [`line-${line.name.replace(/号线|线/g, '').replace(/\s/g, '-')}`]: true
          }"
          :style="{ 
            '--line-color': line.color,
            '--text-color': getLineTextColor(line.color)
          }"
        >
          <!-- 线路信息 -->
          <div class="line-info">
            <div class="line-name-cn">{{ getLineDisplayName(line.name) }}</div>
            <div class="line-name-en">{{ getLineEnglishName(line.name) }}</div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 即时提示框 -->
    <div 
      v-if="buttonTooltip.visible && props.visible" 
      class="button-tooltip"
      :style="{ 
        left: buttonTooltip.x + 'px', 
        top: buttonTooltip.y + 'px' 
      }"
    >
      {{ buttonTooltip.text }}
    </div>
    
    <!-- 简化的测试按钮 - 现在仅用于调试 -->
    <div v-if="false" class="metro-toggle-btn-test">
      <button @click="toggleVisibility" class="simple-btn">
        {{ props.visible ? '隐藏' : '显示' }}线路图
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, onUnmounted, nextTick, watch, computed } from 'vue'
import { gsap } from 'gsap'

// 接收外部传入的可见性状态
interface Props {
  visible?: boolean
  showUnopenedSegments?: boolean // 新增：是否显示未开通线路段
}

const props = withDefaults(defineProps<Props>(), {
  visible: false,
  showUnopenedSegments: false
})

interface Station {
  name: string
  line: string
  order: number
  lng: number
  lat: number
  x: number
  y: number
  lineColor: string
  fork: number // 0: 分叉点, 1: 主线站点, 2: 支线站点
  isOpened: boolean // 是否已开通运营
}

interface Line {
  name: string
  stations: Station[]
  color: string
}

const canvasRef = ref<HTMLCanvasElement>()
const canvasWidth = ref(window.innerWidth)
const canvasHeight = ref(window.innerHeight)
// 使用props中的visible状态，不再需要内部的isVisible
// const isVisible = ref(false) // 删除这行

const stations = ref<Station[]>([])
const lines = ref<Line[]>([])
const animationId = ref<number>()

// 高亮清除定时器引用
const highlightClearTimer = ref<number | null>(null)

// 已开通站点列表
const openedStations = ref<Set<string>>(new Set())

// 交互状态管理
const interactionState = ref({
  offsetX: 0,
  offsetY: 0,
  scale: 1,
  isDragging: false,
  isScaling: false, // 添加缩放状态标志
  lastMouseX: 0,
  lastMouseY: 0,
  hoveredStation: null as Station | null,
  highlightedSegment: null as {
    fromStation: string,
    toStation: string,
    lineName: string
  } | null
})

// 线路显示状态管理
const lineVisibility = ref<Record<string, boolean>>({})

// 悬停状态管理
const hoveredLine = ref<string | null>(null)

// 按钮提示框状态
const buttonTooltip = ref({
  visible: false,
  text: '',
  x: 0,
  y: 0
})

// 计算属性：将线路分成两半
const firstHalfLines = computed(() => {
  const halfIndex = Math.ceil(lines.value.length / 2)
  return lines.value.slice(0, halfIndex)
})

const secondHalfLines = computed(() => {
  const halfIndex = Math.ceil(lines.value.length / 2)
  return lines.value.slice(halfIndex)
})

// 地铁线路颜色映射 - 官方标准色彩
const lineColors: Record<string, string> = {
  '1号线': '#E3002B', // 深红色
  '2号线': '#82BF25', // 绿色
  '3号线': '#FCD600', // 黄色
  '4号线': '#461D84', // 紫色
  '5号线': '#944D9A', // 紫色
  '6号线': '#D40068', // 品红色
  '7号线': '#ED6F00', // 橙色
  '8号线': '#0094D8', // 蓝色
  '9号线': '#87CAED', // 淡蓝色
  '10号线': '#C6AFD4', // 淡紫色
  '11号线': '#871C2B', // 深红色
  '12号线': '#007A60', // 深绿色
  '13号线': '#E999C0', // 粉红色
  '14号线': '#626020', // 橄榄绿
  '15号线': '#BCA886', // 米褐色
  '16号线': '#98D1C0', // 薄荷绿
  '17号线': '#BC796F', // 土褐色
  '18号线': '#C4984F', // 金褐色
  '19号线': '#F5AB78', // 浅橙色
  '20号线': '#009F65', // 翠绿色
  '21号线': '#F7AF00', // 金黄色
  '22号线': '#5F376F', // 深紫色
  '23号线': '#B0D478', // 嫩绿色
  '26号线': '#5F67A9', // 深蓝色
  '嘉闵线': '#6C4B57', // 
  '浦江线': '#B5B5B6', // 灰色
  '南汇线': '#92A6B6', //
  '南枫线': '#556DA5', //
  '示范区线': '#7DAF72', //
  '市域机场线': '#FFFFFF', // 白色（需要黑色描边）
  '磁浮线': '#008B9A', // 青色（与按钮图例上半部分一致）
}

// 动画参数
const animationState = ref({
  time: 0,
  pulseOffset: 0,
  firstRender: true // 添加标志来控制日志输出
})

// 坐标转换函数 - 将经纬度转换为画布坐标
const convertCoordinates = (lng: number, lat: number, bounds: any) => {
  // 计算地理坐标的范围
  const lngRange = bounds.maxLng - bounds.minLng
  const latRange = bounds.maxLat - bounds.minLat
  
  // 使用更大的放大系数来增加站点间距
  const scaleFactor = 3.0 // 增大这个系数来放大整个网络
  const margin = 50 // 边距
  
  // 计算放大后的尺寸
  const scaledWidth = canvasWidth.value * scaleFactor
  const scaledHeight = canvasHeight.value * scaleFactor
  
  // 转换坐标（放大后居中显示）
  const normalizedX = (lng - bounds.minLng) / lngRange
  const normalizedY = (lat - bounds.minLat) / latRange
  
  const x = normalizedX * (scaledWidth - 2 * margin) + margin - (scaledWidth - canvasWidth.value) / 2
  const y = scaledHeight - (normalizedY * (scaledHeight - 2 * margin) + margin) - (scaledHeight - canvasHeight.value) / 2
  
  return { x, y }
}

// 解析CSV数据
const parseCSVData = async () => {
  try {
    // 清空之前的数据，防止重复添加
    lines.value = []
    stations.value = []
    lineVisibility.value = {}
    openedStations.value.clear()
    
    
    // 首先加载已开通站点列表
    try {
      const openedResponse = await fetch('/station_name.csv')
      if (openedResponse.ok) {
        const openedText = await openedResponse.text()
        const openedRows = openedText.split('\n')
        for (const row of openedRows) {
          const stationName = row.trim()
          if (stationName) {
            openedStations.value.add(stationName)
          }
        }
      }
    } catch (error) {
    }
    
    const response = await fetch('/metro_station_location.csv')
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }
    
    const csvText = await response.text()
    const rows = csvText.split('\n').slice(1) // 跳过标题行
    
    const stationData: Station[] = []
    const lineMap = new Map<string, Station[]>()
    
    // 计算坐标边界
    let minLng = Infinity, maxLng = -Infinity
    let minLat = Infinity, maxLat = -Infinity
    
    // 第一遍遍历，解析数据并计算边界
    for (const row of rows) {
      if (!row.trim()) continue
      
      // 更智能的CSV解析 - 处理带引号的字段
      const fields = []
      let current = ''
      let inQuotes = false
      
      for (let i = 0; i < row.length; i++) {
        const char = row[i]
        if (char === '"') {
          inQuotes = !inQuotes
        } else if (char === ',' && !inQuotes) {
          fields.push(current.trim())
          current = ''
        } else {
          current += char
        }
      }
      fields.push(current.trim()) // 添加最后一个字段
      
      if (fields.length < 6) {
        continue
      }
      
      const [sname, rname, order, lat, lng, fork] = fields
      
      const latValue = parseFloat(lat.replace(/"/g, ''))
      const lngValue = parseFloat(lng.replace(/"/g, ''))
      
      if (isNaN(latValue) || isNaN(lngValue)) {
        continue
      }
      
      minLng = Math.min(minLng, lngValue)
      maxLng = Math.max(maxLng, lngValue)
      minLat = Math.min(minLat, latValue)
      maxLat = Math.max(maxLat, latValue)
      
      // 提取线路号
      const lineMatch = rname.replace(/"/g, '').match(/(\d+)号线|(\S+线)/)
      
      if (!lineMatch) {
        continue
      }
      
      // 统一使用基础线路名称，如"1号线"、"嘉闵线"等
      const lineName = lineMatch[1] ? `${lineMatch[1]}号线` : lineMatch[2]
      
      const station: Station = {
        name: sname.replace(/"/g, '').replace('(地铁站)', ''),
        line: lineName,
        order: parseFloat(order.replace(/"/g, '')), // 改为parseFloat以支持小数order
        lng: lngValue,
        lat: latValue,
        x: 0, // 稍后计算
        y: 0, // 稍后计算
        lineColor: lineColors[lineName] || '#64748b',
        fork: parseInt(fork.replace(/"/g, '')), // 解析fork字段
        isOpened: openedStations.value.has(sname.replace(/"/g, '').replace('(地铁站)', '')) // 检查是否已开通
      }
      
      stationData.push(station)
      
      if (!lineMap.has(lineName)) {
        lineMap.set(lineName, [])
      }
      lineMap.get(lineName)!.push(station)
    }
    
    const bounds = { minLng, maxLng, minLat, maxLat }
    
    // 第二遍遍历，计算屏幕坐标
    for (const station of stationData) {
      const coords = convertCoordinates(station.lng, station.lat, bounds)
      station.x = coords.x
      station.y = coords.y
    }
    
    // 按照order和fork字段组织线路数据
    for (const [lineName, lineStations] of lineMap) {
      // 按order排序所有站点
      lineStations.sort((a, b) => {
        if (a.order !== b.order) {
          return a.order - b.order
        }
        // order相同时，主线(fork=1)优先，然后分叉点(fork=0)，最后支线(fork=2)
        return a.fork - b.fork
      })
      
      // 获取基础线路号用于颜色
      const baseLineMatch = lineName.match(/(\d+)号线/)
      const baseLineName = baseLineMatch ? `${baseLineMatch[1]}号线` : lineName
      const lineColor = lineColors[baseLineName] || '#64748b'
      
      // 创建统一的线路对象，包含所有站点（主线、分叉点、支线）
      lines.value.push({
        name: lineName,
        stations: lineStations,
        color: lineColor
      })
    }
    
    stations.value = stationData
    
    // 初始化线路显示状态（默认全部显示）
    const visibilityState: Record<string, boolean> = {}
    for (const line of lines.value) {
      visibilityState[line.name] = true
    }
    lineVisibility.value = visibilityState
    
    // 设置初始视图参数，让网络居中并适合屏幕
    interactionState.value.scale = 0.4 // 初始缩放比例
    interactionState.value.offsetX = canvasWidth.value * 0.1 // 水平居中偏移
    interactionState.value.offsetY = canvasHeight.value * 0.1 // 垂直居中偏移
    
    lines.value.forEach(line => {
    })
    
  } catch (error) {
    if (error instanceof Error) {
    }
  }
}

// 节流函数
const throttle = (func: Function, limit: number) => {
  let inThrottle: boolean = false
  return function(this: any, ...args: any[]) {
    if (!inThrottle) {
      func.apply(this, args)
      inThrottle = true
      setTimeout(() => inThrottle = false, limit)
    }
  }
}

// 鼠标事件处理
const handleMouseDown = (event: MouseEvent) => {
  if (!props.visible) return
  
  interactionState.value.isDragging = true
  interactionState.value.lastMouseX = event.clientX
  interactionState.value.lastMouseY = event.clientY
  
  if (canvasRef.value) {
    canvasRef.value.style.cursor = 'grabbing'
  }
}

// 优化的鼠标移动处理 - 节流到60fps
const handleMouseMove = throttle((event: MouseEvent) => {
  if (!props.visible) return
  
  if (interactionState.value.isDragging) {
    // 拖拽移动
    const deltaX = event.clientX - interactionState.value.lastMouseX
    const deltaY = event.clientY - interactionState.value.lastMouseY
    
    interactionState.value.offsetX += deltaX
    interactionState.value.offsetY += deltaY
    
    interactionState.value.lastMouseX = event.clientX
    interactionState.value.lastMouseY = event.clientY
  } else {
    // 检测悬停的站点 - 降低频率
    const rect = canvasRef.value?.getBoundingClientRect()
    if (!rect) return
    
    const mouseX = (event.clientX - rect.left - interactionState.value.offsetX) / interactionState.value.scale
    const mouseY = (event.clientY - rect.top - interactionState.value.offsetY) / interactionState.value.scale
    
    // 查找最近的站点
    let closestStation: Station | null = null
    let minDistance = Infinity
    
    for (const station of stations.value) {
      const distance = Math.sqrt(
        Math.pow(station.x - mouseX, 2) + Math.pow(station.y - mouseY, 2)
      )
      
      if (distance < 15 && distance < minDistance) {
        minDistance = distance
        closestStation = station
      }
    }
    
    interactionState.value.hoveredStation = closestStation
    
    if (canvasRef.value) {
      canvasRef.value.style.cursor = closestStation ? 'pointer' : 'grab'
    }
  }
}, 16) // 限制为60fps

const handleMouseUp = (event: MouseEvent) => {
  if (!props.visible) return
  
  interactionState.value.isDragging = false
  
  if (canvasRef.value) {
    canvasRef.value.style.cursor = 'grab'
  }
}

// GSAP动画状态
const zoomAnimation = ref<gsap.core.Tween | null>(null)

// 优化的滚轮缩放处理 - 使用GSAP平滑缩放
const handleWheel = throttle((event: WheelEvent) => {
  if (!props.visible) return
  
  event.preventDefault()
  
  const rect = canvasRef.value?.getBoundingClientRect()
  if (!rect) return
  
  // 获取鼠标在画布上的位置
  const mouseX = event.clientX - rect.left
  const mouseY = event.clientY - rect.top
  
  // 计算缩放前鼠标在世界坐标中的位置
  const worldX = (mouseX - interactionState.value.offsetX) / interactionState.value.scale
  const worldY = (mouseY - interactionState.value.offsetY) / interactionState.value.scale
  
  // 增大缩放因子，减少滚轮行程
  const scaleFactor = event.deltaY > 0 ? 0.85 : 1.18  // 从0.95/1.05增大到0.85/1.18，更敏感
  const targetScale = Math.max(0.2, Math.min(5, interactionState.value.scale * scaleFactor))
  
  // 计算目标偏移量
  const targetOffsetX = mouseX - worldX * targetScale
  const targetOffsetY = mouseY - worldY * targetScale
  
  // 如果有正在进行的缩放动画，先终止它
  if (zoomAnimation.value) {
    zoomAnimation.value.kill()
  }
  
  // 设置缩放状态
  interactionState.value.isScaling = true
  
  // 使用GSAP创建平滑的缩放动画
  zoomAnimation.value = gsap.to(interactionState.value, {
    scale: targetScale,
    offsetX: targetOffsetX,
    offsetY: targetOffsetY,
    duration: 0.3, // 动画持续时间
    ease: "power2.out", // 使用平滑的缓动函数
    onComplete: () => {
      interactionState.value.isScaling = false
      zoomAnimation.value = null
    }
  })
}, 16) // 保持16ms的节流，让动画更平滑

const handleStationClick = (station: Station) => {
  // 可以在这里添加更多交互逻辑，比如显示站点详情
}

const handleCanvasClick = (event: MouseEvent) => {
  if (!props.visible || interactionState.value.isDragging) return
  
  if (interactionState.value.hoveredStation) {
    handleStationClick(interactionState.value.hoveredStation)
  }
}

// 辅助函数：获取线路显示名称（不包含始末站点）
const getLineDisplayName = (lineName: string): string => {
  const match = lineName.match(/(\d+)号线/)
  return match ? `${match[1]}号线` : lineName
}

// 辅助函数：获取线路编号（只显示数字）
const getLineNumber = (lineName: string): string => {
  const match = lineName.match(/(\d+)号线/)
  if (match) {
    return match[1]
  }
  // 对于特殊线路（如磁浮线），提取数字或返回简写
  const numbers = lineName.replace(/[^\d]/g, '')
  if (numbers) return numbers
  if (lineName.includes('磁浮')) return 'M'
  if (lineName.includes('浦江')) return 'P'
  if (lineName.includes('嘉闵')) return 'J'
  return lineName.substring(0, 1).toUpperCase()
}

// 辅助函数：获取线路完整英文名称
const getLineEnglishName = (lineName: string): string => {
  const match = lineName.match(/(\d+)号线/)
  if (match) {
    return `Line ${match[1]}`
  }
  
  // 非数字线路的完整英文名称映射
  const englishNames: Record<string, string> = {
    '磁浮线': 'Maglev Line',
    '浦江线': 'Pujiang Line',
    '嘉闵线': 'Jia-min Line',
    '市域机场线': 'Airport Link Line',
    '南汇线': 'Nanhui Line',
    '南枫线': 'Nanfeng Line',
    '示范区线': 'Demo Area Line'
  }
  
  return englishNames[lineName] || lineName
}

// 辅助函数：根据背景色计算文字颜色（提供最佳对比度）
const getLineTextColor = (backgroundColor: string): string => {
  // 特殊处理市域机场线
  if (backgroundColor === '#FFFFFF') {
    return '#000000'
  }
  
  // 特殊处理磁浮线
  if (backgroundColor === '#008B9A') {
    return '#FFFFFF'
  }
  
  // 解析十六进制颜色
  const hex = backgroundColor.replace('#', '')
  const r = parseInt(hex.substr(0, 2), 16)
  const g = parseInt(hex.substr(2, 2), 16)
  const b = parseInt(hex.substr(4, 2), 16)
  
  // 计算亮度（使用相对亮度公式）
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  
  // 根据亮度返回黑色或白色文字
  return luminance > 0.5 ? '#000000' : '#ffffff'
}

// 线路显示控制函数
const toggleLineVisibility = (lineName: string) => {
  lineVisibility.value[lineName] = !lineVisibility.value[lineName]
}

// 悬停控制函数
const handleLineHover = (lineName: string | null) => {
  hoveredLine.value = lineName
}

// 按钮悬停控制函数
const handleLineButtonHover = (lineName: string, event: MouseEvent) => {
  // 立即显示线路悬停效果
  hoveredLine.value = lineName
  
  // 计算提示框位置
  const rect = (event.target as HTMLElement).getBoundingClientRect()
  const tooltipText = `${getLineDisplayName(lineName)} - 点击${lineVisibility.value[lineName] ? '隐藏' : '显示'}，悬停单独显示`
  
  buttonTooltip.value = {
    visible: true,
    text: tooltipText,
    x: rect.left + rect.width / 2,
    y: rect.top - 8 // 在按钮上方显示
  }
}

const handleLineButtonLeave = () => {
  hoveredLine.value = null
  buttonTooltip.value.visible = false
}

// 检查线路是否应该显示
const shouldShowLine = (lineName: string): boolean => {
  // 如果有悬停线路，只显示悬停的线路
  if (hoveredLine.value) {
    return lineName === hoveredLine.value
  }
  // 否则按正常显示状态显示
  return lineVisibility.value[lineName]
}

// 检查站点是否应该显示
const shouldShowStation = (station: Station): boolean => {
  return shouldShowLine(station.line)
}

// 缓存绘制状态
const drawingCache = ref(new Map<string, {
  path: Path2D,
  needsUpdate: boolean
}>())

// 绘制线路（支持分叉）- 优化版本，处理并线情况和未开通线路段
const drawLine = (ctx: CanvasRenderingContext2D, line: Line, time?: number) => {
  if (line.stations.length < 2) return
  
  const cacheKey = `${line.name}_${line.stations.length}`
  
  // 检查缓存
  let cached = drawingCache.value.get(cacheKey)
  if (!cached || cached.needsUpdate) {
    // 重新计算路径
    const path = new Path2D()
    generateLinePath(path, line)
    cached = { path, needsUpdate: false }
    drawingCache.value.set(cacheKey, cached)
  }
  
  // 检查是否为并线线路，需要分段处理
  const isParallelLine = (line.name === '3号线' || line.name === '4号线')
  
  if (isParallelLine) {
    // 对于并线线路，分段绘制：并线区段偏移，其他区段正常
    drawParallelLine(ctx, line, cached.path)
  } else {
    // 非并线线路正常绘制
    drawNormalLine(ctx, line, cached.path)
  }
  
  // 只有在启用未开通线路段显示时才绘制
  if (props.showUnopenedSegments) {
    drawUnopenedSegments(ctx, line, time)
  }
  
  // 绘制高亮线路段
  if (interactionState.value.highlightedSegment && 
      interactionState.value.highlightedSegment.lineName === line.name) {
    drawHighlightedSegment(ctx, line, interactionState.value.highlightedSegment, time)
  }
}

// 绘制普通线路
const drawNormalLine = (ctx: CanvasRenderingContext2D, line: Line, path: Path2D) => {
  // 当显示未开通路段时，已开通线路需要弱化显示
  const isDimmed = props.showUnopenedSegments
  const alphaMultiplier = isDimmed ? 0.3 : 1 // 弱化时透明度降低到30%
  const lineWidthMultiplier = isDimmed ? 0.7 : 1 // 弱化时线宽也减少
  
  // 特殊处理磁浮线的对半分颜色
  if (line.name === '磁浮线') {
    // 先绘制轻微的白色阴影
    ctx.strokeStyle = '#ffffff'
    ctx.lineWidth = 5 * lineWidthMultiplier
    ctx.globalAlpha = 0.2 * alphaMultiplier
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.stroke(path)
    
    // 绘制对半分的线路：通过两条平行的半宽线实现
    const halfWidth = 2 * lineWidthMultiplier // 每条线的宽度
    
    // 创建上半部分路径（青色）
    ctx.save()
    ctx.translate(0, -halfWidth / 2) // 向上偏移
    ctx.strokeStyle = '#008B9A'
    ctx.lineWidth = halfWidth
    ctx.globalAlpha = 1 * alphaMultiplier
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.stroke(path)
    ctx.restore()
    
    // 创建下半部分路径（橙色）
    ctx.save()
    ctx.translate(0, halfWidth / 2) // 向下偏移
    ctx.strokeStyle = '#FF9147'
    ctx.lineWidth = halfWidth
    ctx.globalAlpha = 1 * alphaMultiplier
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.stroke(path)
    ctx.restore()
  }
  // 特殊处理市域机场线的白色+黑色描边
  else if (line.name === '市域机场线') {
    // 先绘制轻微的白色阴影
    ctx.strokeStyle = '#ffffff'
    ctx.lineWidth = 5 * lineWidthMultiplier
    ctx.globalAlpha = 0.2 * alphaMultiplier
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.stroke(path)
    
    // 先绘制黑色描边（增加粗细）
    ctx.strokeStyle = '#000000'
    ctx.lineWidth = 4 * lineWidthMultiplier
    ctx.globalAlpha = 1 * alphaMultiplier
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.stroke(path)
    
    // 再绘制白色主线
    ctx.strokeStyle = '#FFFFFF'
    ctx.lineWidth = 2 * lineWidthMultiplier
    ctx.globalAlpha = 1 * alphaMultiplier
    ctx.stroke(path)
  }
  // 其他线路的正常绘制
  else {
    // 先绘制轻微的白色阴影
    ctx.strokeStyle = '#ffffff'
    ctx.lineWidth = 5 * lineWidthMultiplier
    ctx.globalAlpha = 0.2 * alphaMultiplier
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.stroke(path)
    
    // 使用缓存的路径绘制主线条
    ctx.strokeStyle = line.color
    ctx.lineWidth = 4 * lineWidthMultiplier
    ctx.globalAlpha = 1 * alphaMultiplier
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.stroke(path)
  }
  
  ctx.globalAlpha = 1
}

// 绘制并线线路（3号线和4号线）
const drawParallelLine = (ctx: CanvasRenderingContext2D, line: Line, path: Path2D) => {
  // 3号线和4号线并线区段的站点名称（根据实际情况定义）
  const parallelStations = [
    '宜山路', '虹桥路', '延安西路', '中山公园', '金沙江路', 
    '曹杨路', '镇坪路', '中潭路', '上海火车站'
  ]
  
  // 找到并线区段的起始和结束索引
  let parallelStartIndex = -1
  let parallelEndIndex = -1
  
  for (let i = 0; i < line.stations.length; i++) {
    const station = line.stations[i]
    if (parallelStations.includes(station.name)) {
      if (parallelStartIndex === -1) {
        parallelStartIndex = i
      }
      parallelEndIndex = i
    }
  }
  
  if (parallelStartIndex === -1 || parallelEndIndex === -1) {
    // 如果没有找到并线区段，正常绘制整条线路
    drawLineSegment(ctx, line, path)
    return
  }
  
  // 绘制三个连续的部分：
  // 1. 并线前的部分（正常绘制）
  if (parallelStartIndex > 0) {
    const preParallelStations = line.stations.slice(0, parallelStartIndex + 1)
    const preParallelPath = createSegmentPath(preParallelStations)
    drawLineSegment(ctx, line, preParallelPath)
  }
  
  // 2. 并线区段（偏移绘制）
  const parallelSegmentStations = line.stations.slice(parallelStartIndex, parallelEndIndex + 1)
  const parallelPath = createSegmentPath(parallelSegmentStations)
  
  ctx.save()
  const offset = line.name === '3号线' ? -2 : 2
  ctx.translate(offset, offset)
  drawLineSegment(ctx, line, parallelPath)
  ctx.restore()
  
  // 3. 并线后的部分（正常绘制）
  if (parallelEndIndex < line.stations.length - 1) {
    const postParallelStations = line.stations.slice(parallelEndIndex, line.stations.length)
    const postParallelPath = createSegmentPath(postParallelStations)
    drawLineSegment(ctx, line, postParallelPath)
  }
}

// 创建区段路径
const createSegmentPath = (stations: Station[]): Path2D => {
  const path = new Path2D()
  if (stations.length < 2) return path
  
  path.moveTo(stations[0].x, stations[0].y)
  for (let i = 1; i < stations.length; i++) {
    path.lineTo(stations[i].x, stations[i].y)
  }
  
  return path
}

// 绘制线段
const drawLineSegment = (ctx: CanvasRenderingContext2D, line: Line, path: Path2D) => {
  // 检查是否正在显示未开通路段，如果是则弱化已开通线路
  const shouldDimOpened = props.showUnopenedSegments
  const opacity = shouldDimOpened ? 0.3 : 1 // 显示未开通路段时弱化已开通线路
  
  // 先绘制轻微的白色阴影
  ctx.strokeStyle = '#ffffff'
  ctx.lineWidth = 5
  ctx.globalAlpha = 0.2 * opacity
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.stroke(path)
  
  // 特殊处理磁浮线的对半分颜色
  if (line.name === '磁浮线') {
    // 绘制对半分的线路：通过两条平行的半宽线实现
    const halfWidth = 2 // 每条线的宽度
    
    // 创建上半部分路径（青色）
    ctx.save()
    ctx.translate(0, -halfWidth / 2) // 向上偏移
    ctx.strokeStyle = '#008B9A'
    ctx.lineWidth = halfWidth
    ctx.globalAlpha = opacity
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.stroke(path)
    ctx.restore()
    
    // 创建下半部分路径（橙色）
    ctx.save()
    ctx.translate(0, halfWidth / 2) // 向下偏移
    ctx.strokeStyle = '#FF9147'
    ctx.lineWidth = halfWidth
    ctx.globalAlpha = opacity
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.stroke(path)
    ctx.restore()
  } else {
    // 绘制其他线路的主线条
    ctx.strokeStyle = line.color
    ctx.lineWidth = 4
    ctx.globalAlpha = opacity
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.stroke(path)
  }
  
  ctx.globalAlpha = 1
}

// 绘制未开通线路段
const drawUnopenedSegments = (ctx: CanvasRenderingContext2D, line: Line, time?: number) => {
  // 只有在显示未开通线路段时才绘制
  if (!props.showUnopenedSegments) return
  
  // 按order排序站点
  const sortedStations = [...line.stations].sort((a, b) => {
    if (a.order !== b.order) {
      return a.order - b.order
    }
    return a.fork - b.fork
  })
  
  // 找出未开通的线路段
  for (let i = 0; i < sortedStations.length - 1; i++) {
    const currentStation = sortedStations[i]
    const nextStation = sortedStations[i + 1]
    
    // 如果任一站点未开通，则这个线路段为未开通
    if (!currentStation.isOpened || !nextStation.isOpened) {
      ctx.save()
      
      // 强烈的发光效果 - 多层渲染
      const currentTime = time || animationState.value.time
      const pulse = Math.sin(currentTime * 0.003) * 0.5 + 0.5 // 0到1之间的脉动
      
      // 外层发光 - 最强的红色光晕
      ctx.strokeStyle = '#ff0000'
      ctx.lineWidth = 20 + pulse * 8 // 动态变化的光晕大小
      ctx.globalAlpha = 0.15 + pulse * 0.1 // 动态透明度
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.filter = 'blur(8px)' // 模糊效果增强发光
      ctx.setLineDash([]) // 实线光晕
      
      ctx.beginPath()
      ctx.moveTo(currentStation.x, currentStation.y)
      ctx.lineTo(nextStation.x, nextStation.y)
      ctx.stroke()
      
      // 中层发光
      ctx.strokeStyle = '#ff3333'
      ctx.lineWidth = 12 + pulse * 4
      ctx.globalAlpha = 0.3 + pulse * 0.15
      ctx.filter = 'blur(4px)'
      
      ctx.beginPath()
      ctx.moveTo(currentStation.x, currentStation.y)
      ctx.lineTo(nextStation.x, nextStation.y)
      ctx.stroke()
      
      // 内层发光
      ctx.strokeStyle = '#ff6666'
      ctx.lineWidth = 8 + pulse * 2
      ctx.globalAlpha = 0.5 + pulse * 0.2
      ctx.filter = 'blur(2px)'
      
      ctx.beginPath()
      ctx.moveTo(currentStation.x, currentStation.y)
      ctx.lineTo(nextStation.x, nextStation.y)
      ctx.stroke()
      
      // 核心线条 - 鲜艳的红色虚线
      ctx.filter = 'none' // 清除模糊
      ctx.strokeStyle = '#ff0000'
      ctx.lineWidth = 6
      ctx.globalAlpha = 0.9 + pulse * 0.1
      ctx.setLineDash([12, 8]) // 虚线样式
      ctx.lineDashOffset = currentTime * 0.05 % 20 // 动态虚线偏移，制造流动效果
      
      ctx.beginPath()
      ctx.moveTo(currentStation.x, currentStation.y)
      ctx.lineTo(nextStation.x, nextStation.y)
      ctx.stroke()
      
      ctx.restore()
    }
  }
}

// 绘制高亮线路段
const drawHighlightedSegment = (ctx: CanvasRenderingContext2D, line: Line, segment: {
  fromStation: string,
  toStation: string,
  lineName: string
}, time?: number) => {
  // 找到线路段的起点和终点站点
  const fromStationObj = line.stations.find(station => station.name === segment.fromStation)
  const toStationObj = line.stations.find(station => station.name === segment.toStation)
  
  if (!fromStationObj || !toStationObj) {
    return
  }
  
  // 按order排序，确定方向
  const sortedStations = [...line.stations].sort((a, b) => a.order - b.order)
  const fromIndex = sortedStations.findIndex(s => s.name === segment.fromStation)
  const toIndex = sortedStations.findIndex(s => s.name === segment.toStation)
  
  if (fromIndex === -1 || toIndex === -1) {
    return
  }
  
  // 确定线路段的方向
  const startIndex = Math.min(fromIndex, toIndex)
  const endIndex = Math.max(fromIndex, toIndex)
  
  ctx.save()
  
  // 创建高亮效果的时间动画
  const currentTime = time || animationState.value.time
  const pulse = Math.sin(currentTime * 0.006) * 0.5 + 0.5 // 更慢的脉动效果
  const glow = Math.sin(currentTime * 0.008) * 0.3 + 0.7 // 更慢的发光强度变化
  
  // 绘制线路段的各个小段
  for (let i = startIndex; i < endIndex; i++) {
    const currentStation = sortedStations[i]
    const nextStation = sortedStations[i + 1]
    
    // 外层发光 - 黄色光晕
    ctx.strokeStyle = '#fbbf24'
    ctx.lineWidth = 25 + pulse * 10 // 动态变化的光晕大小
    ctx.globalAlpha = 0.2 * glow // 动态透明度
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.filter = 'blur(10px)' // 模糊效果增强发光
    ctx.setLineDash([]) // 实线光晕
    
    ctx.beginPath()
    ctx.moveTo(currentStation.x, currentStation.y)
    ctx.lineTo(nextStation.x, nextStation.y)
    ctx.stroke()
    
    // 中层发光 - 橙色
    ctx.strokeStyle = '#f59e0b'
    ctx.lineWidth = 15 + pulse * 6
    ctx.globalAlpha = 0.4 * glow
    ctx.filter = 'blur(5px)'
    
    ctx.beginPath()
    ctx.moveTo(currentStation.x, currentStation.y)
    ctx.lineTo(nextStation.x, nextStation.y)
    ctx.stroke()
    
    // 内层发光 - 金色
    ctx.strokeStyle = '#eab308'
    ctx.lineWidth = 10 + pulse * 3
    ctx.globalAlpha = 0.6 * glow
    ctx.filter = 'blur(2px)'
    
    ctx.beginPath()
    ctx.moveTo(currentStation.x, currentStation.y)
    ctx.lineTo(nextStation.x, nextStation.y)
    ctx.stroke()
    
    // 核心线条 - 亮黄色实线
    ctx.filter = 'none' // 清除模糊
    ctx.strokeStyle = '#fde047'
    ctx.lineWidth = 8
    ctx.globalAlpha = 0.9 + pulse * 0.1
    ctx.setLineDash([]) // 实线
    
    ctx.beginPath()
    ctx.moveTo(currentStation.x, currentStation.y)
    ctx.lineTo(nextStation.x, nextStation.y)
    ctx.stroke()
  }
  
  ctx.restore()
}

// 生成线路路径 - 基于fork字段处理分叉
const generateLinePath = (path: Path2D, line: Line) => {
  if (line.stations.length < 2) return
  
  // 按order排序所有站点
  const sortedStations = [...line.stations].sort((a, b) => {
    if (a.order !== b.order) {
      return a.order - b.order
    }
    // order相同时，按fork排序：1(主线) -> 0(分叉点) -> 2(支线)
    return a.fork - b.fork
  })
  
  // 分离主线、分叉点和支线
  const mainLineStations: Station[] = []
  const branchStations: Station[] = []
  const forkPoints: Station[] = []
  
  for (const station of sortedStations) {
    switch (station.fork) {
      case 0: // 分叉点
        forkPoints.push(station)
        break
      case 1: // 主线站点
        mainLineStations.push(station)
        break
      case 2: // 支线站点
        branchStations.push(station)
        break
    }
  }
  
  // 如果没有分叉，使用简单路径
  if (forkPoints.length === 0 || branchStations.length === 0) {
    generateSimplePath(path, sortedStations)
    return
  }
  
  // 有分叉的情况：先绘制主线（包括分叉点）
  const mainLineWithForks = [...mainLineStations, ...forkPoints]
  mainLineWithForks.sort((a, b) => a.order - b.order)
  generateSimplePath(path, mainLineWithForks)
  
  // 然后为每个分叉点绘制到支线的路径
  for (const forkPoint of forkPoints) {
    // 找到从这个分叉点开始的支线站点
    const relatedBranchStations = branchStations
      .filter(station => station.order > forkPoint.order)
      .sort((a, b) => a.order - b.order)
    
    if (relatedBranchStations.length > 0) {
      // 从分叉点开始绘制支线
      path.moveTo(forkPoint.x, forkPoint.y)
      for (const station of relatedBranchStations) {
        path.lineTo(station.x, station.y)
      }
    }
  }
}

// 生成简单路径
const generateSimplePath = (path: Path2D, stations: Station[]) => {
  if (stations.length < 2) return
  
  path.moveTo(stations[0].x, stations[0].y)
  for (let i = 1; i < stations.length; i++) {
    path.lineTo(stations[i].x, stations[i].y)
  }
}

// 生成分叉路径 - 递归构建每个分支
const generateBranchedPath = (path: Path2D, stations: Station[]) => {
  // 按order分组站点
  const orderGroups = new Map<number, Station[]>()
  
  stations.forEach(station => {
    if (!orderGroups.has(station.order)) {
      orderGroups.set(station.order, [])
    }
    orderGroups.get(station.order)!.push(station)
  })
  
  const sortedOrders = Array.from(orderGroups.keys()).sort((a, b) => a - b)
  
  // 找到分叉点：最后一个只有单个站点的order
  let branchingPointOrder = -1
  let branchingStation: Station | null = null
  
  for (let i = 0; i < sortedOrders.length; i++) {
    const order = sortedOrders[i]
    const stationsAtOrder = orderGroups.get(order)!
    
    if (stationsAtOrder.length === 1) {
      branchingPointOrder = order
      branchingStation = stationsAtOrder[0]
    } else {
      break
    }
  }
  
  // 绘制主干路径（到分叉点）
  const mainStations: Station[] = []
  for (let i = 0; i < sortedOrders.length; i++) {
    const order = sortedOrders[i]
    if (order > branchingPointOrder) break
    
    const stationsAtOrder = orderGroups.get(order)!
    if (stationsAtOrder.length === 1) {
      mainStations.push(stationsAtOrder[0])
    }
  }
  
  if (mainStations.length >= 2) {
    path.moveTo(mainStations[0].x, mainStations[0].y)
    for (let i = 1; i < mainStations.length; i++) {
      path.lineTo(mainStations[i].x, mainStations[i].y)
    }
  }
  
  // 递归函数：构建从指定起点开始的分支路径
  const buildBranchPath = (startStation: Station, remainingOrders: number[]): Station[] => {
    if (remainingOrders.length === 0) return [startStation]
    
    const branchPath = [startStation]
    let currentStation = startStation
    
    for (const order of remainingOrders) {
      const candidates = orderGroups.get(order)!
      
      if (candidates.length === 1) {
        // 只有一个站点，直接添加
        const nextStation = candidates[0]
        branchPath.push(nextStation)
        currentStation = nextStation
      } else {
        // 多个站点，选择距离当前站点最近且未被当前分支使用的
        let closest: Station | null = null
        let minDistance = Infinity
        
        for (const candidate of candidates) {
          // 检查该候选站点是否已经在当前分支路径中
          if (branchPath.find(s => s.name === candidate.name)) continue
          
          const distance = Math.sqrt(
            Math.pow(currentStation.x - candidate.x, 2) + 
            Math.pow(currentStation.y - candidate.y, 2)
          )
          if (distance < minDistance) {
            minDistance = distance
            closest = candidate
          }
        }
        
        if (closest) {
          branchPath.push(closest)
          currentStation = closest
        } else {
          // 没有合适的下一站点，分支结束
          break
        }
      }
    }
    
    return branchPath
  }
  
  // 处理分叉
  if (branchingStation && branchingPointOrder !== -1) {
    const branchOrders = sortedOrders.filter(order => order > branchingPointOrder)
    if (branchOrders.length === 0) return
    
    const firstBranchOrder = branchOrders[0]
    const branchStarts = orderGroups.get(firstBranchOrder)!
    const remainingOrders = branchOrders.slice(1) // 排除第一个order
    
    // 为每个分支起点构建独立路径
    branchStarts.forEach(branchStart => {
      const branchPath = buildBranchPath(branchStart, remainingOrders)
      
      // 绘制分支路径
      if (branchPath.length > 0) {
        path.moveTo(branchingStation.x, branchingStation.y)
        path.lineTo(branchPath[0].x, branchPath[0].y)
        
        for (let i = 1; i < branchPath.length; i++) {
          path.lineTo(branchPath[i].x, branchPath[i].y)
        }
      }
    })
  }
}

// 绘制站点 - 包含悬停效果和开通状态区分
const drawStation = (ctx: CanvasRenderingContext2D, station: Station, time: number) => {
  // 检查是否为悬停站点
  const isHovered = interactionState.value.hoveredStation?.name === station.name
  
  // 当显示未开通路段时的特殊处理
  const showingUnopened = props.showUnopenedSegments
  
  // 基础大小
  let stationSize = 3
  let outerSize = stationSize + 2
  
  // 悬停效果
  if (isHovered) {
    // 悬停时放大
    stationSize = 5
    outerSize = stationSize + 3
    
    // 添加脉动效果
    const pulse = Math.sin(time * 0.008) * 0.5 + 0.5 // 0到1之间的正弦波
    const glowSize = outerSize + (pulse * 3)
    
    // 绘制发光效果
    ctx.fillStyle = station.lineColor
    ctx.globalAlpha = 0.3 * (1 - pulse * 0.5) // 随脉动变化透明度
    ctx.beginPath()
    ctx.arc(station.x, station.y, glowSize, 0, Math.PI * 2)
    ctx.fill()
  }
  
  // 未开通站点的强烈发光效果
  if (!station.isOpened && showingUnopened) {
    const pulse = Math.sin(time * 0.004) * 0.5 + 0.5 // 慢一点的脉动
    
    // 多层发光效果
    // 外层发光
    ctx.fillStyle = '#ff0000'
    ctx.globalAlpha = 0.2 + pulse * 0.15
    ctx.filter = 'blur(12px)'
    ctx.beginPath()
    ctx.arc(station.x, station.y, 15 + pulse * 5, 0, Math.PI * 2)
    ctx.fill()
    
    // 中层发光
    ctx.fillStyle = '#ff3333'
    ctx.globalAlpha = 0.3 + pulse * 0.2
    ctx.filter = 'blur(6px)'
    ctx.beginPath()
    ctx.arc(station.x, station.y, 10 + pulse * 3, 0, Math.PI * 2)
    ctx.fill()
    
    // 内层发光
    ctx.fillStyle = '#ff6666'
    ctx.globalAlpha = 0.4 + pulse * 0.3
    ctx.filter = 'blur(3px)'
    ctx.beginPath()
    ctx.arc(station.x, station.y, 6 + pulse * 2, 0, Math.PI * 2)
    ctx.fill()
    
    ctx.filter = 'none' // 清除模糊效果
  }
  
  // 站点外圈
  if (station.isOpened) {
    // 已开通站点：白色外圈，显示未开通路段时弱化
    ctx.fillStyle = '#ffffff'
    const baseAlpha = isHovered ? 1 : 0.9
    ctx.globalAlpha = showingUnopened ? baseAlpha * 0.4 : baseAlpha
  } else {
    // 未开通站点：红色外圈，显示未开通路段时增强
    ctx.fillStyle = '#ff0000'
    const baseAlpha = isHovered ? 1 : 0.8
    ctx.globalAlpha = showingUnopened ? Math.min(1, baseAlpha * 1.5) : baseAlpha
    
    // 未开通站点在显示模式下使用更大的外圈
    if (showingUnopened) {
      outerSize = Math.max(outerSize, stationSize + 4)
    }
  }
  
  ctx.beginPath()
  ctx.arc(station.x, station.y, outerSize, 0, Math.PI * 2)
  ctx.fill()
  
  // 站点内圈
  if (station.isOpened) {
    // 已开通站点：使用线路颜色，显示未开通路段时弱化
    ctx.fillStyle = station.lineColor
    ctx.globalAlpha = showingUnopened ? 0.5 : 1
  } else {
    // 未开通站点：使用明亮的红色
    ctx.fillStyle = showingUnopened ? '#ff0000' : '#666666'
    ctx.globalAlpha = 1
  }
  
  ctx.beginPath()
  ctx.arc(station.x, station.y, stationSize, 0, Math.PI * 2)
  ctx.fill()
  
  // 悬停时添加额外的高亮边框
  if (isHovered) {
    ctx.strokeStyle = station.isOpened ? station.lineColor : '#ff4444'
    ctx.lineWidth = 2
    ctx.globalAlpha = 0.8
    ctx.beginPath()
    ctx.arc(station.x, station.y, outerSize + 1, 0, Math.PI * 2)
    ctx.stroke()
  }
  
  // 重置透明度
  ctx.globalAlpha = 1
}

// 简化的站名绘制函数 - 提高性能
const drawStationName = (ctx: CanvasRenderingContext2D, station: Station) => {
  // 基于当前缩放比例动态调整字体大小
  const currentScale = interactionState.value.scale
  
  // 优化：使用简化的字体大小计算，减少Math运算
  let fontSize = 11
  if (currentScale > 1.5) {
    fontSize = 8  // 高缩放时使用固定小字体
  } else if (currentScale > 1) {
    fontSize = 10 // 中等缩放时使用中等字体
  } else if (currentScale < 0.6) {
    fontSize = 13 // 低缩放时使用大字体
  }
  
  // 简化描边粗细计算
  const strokeWidth = currentScale > 1.5 ? 1 : (currentScale > 1 ? 2 : 3)
  
  // 使用动态字体大小
  ctx.font = `${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'top'
  
  // 直接使用白色字体，添加描边提高可读性
  ctx.strokeStyle = '#000000'
  ctx.lineWidth = strokeWidth
  ctx.strokeText(station.name, station.x, station.y + 8)
  
  ctx.fillStyle = '#ffffff'
  ctx.fillText(station.name, station.x, station.y + 8)
}

// 绘制换乘站连接线
const drawTransferConnections = (ctx: CanvasRenderingContext2D, stations: Station[]) => {
  if (stations.length < 2) return
  
  // 设置连接线样式
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)'
  ctx.lineWidth = 2
  ctx.lineCap = 'round'
  ctx.globalAlpha = 0.6
  
  // 如果只有两个站点，直接连接
  if (stations.length === 2) {
    ctx.beginPath()
    ctx.moveTo(stations[0].x, stations[0].y)
    ctx.lineTo(stations[1].x, stations[1].y)
    ctx.stroke()
  } else {
    // 多个站点：连接到中心点
    const centerX = stations.reduce((sum, s) => sum + s.x, 0) / stations.length
    const centerY = stations.reduce((sum, s) => sum + s.y, 0) / stations.length
    
    ctx.beginPath()
    for (const station of stations) {
      ctx.moveTo(station.x, station.y)
      ctx.lineTo(centerX, centerY)
    }
    ctx.stroke()
  }
  
  ctx.globalAlpha = 1
}

// 绘制换乘站统一站名
const drawTransferStationName = (ctx: CanvasRenderingContext2D, station: Station) => {
  // 动态调整站名显示阈值：缩放比例越大，显示条件越宽松
  const currentScale = interactionState.value.scale
  let showThreshold = 0.6
  
  if (currentScale > 1.5) {
    // 高度放大时，降低显示阈值，让更多站名显示
    showThreshold = 0.3
  } else if (currentScale > 1) {
    // 中度放大时，适中的显示阈值
    showThreshold = 0.4
  }
  
  if (currentScale > showThreshold) {
    drawStationName(ctx, station)
  }
}

// 切换显示状态 - 现在仅用于调试
const toggleVisibility = () => {
  // 不再改变状态，因为状态由父组件控制
}

// 主渲染函数
const render = (time: number) => {
  const canvas = canvasRef.value
  if (!canvas) return
  
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  
  // 更新动画状态（即使不可见也要更新，保持动画连续性）
  animationState.value.time = time
  
  // 只有在可见时才进行绘制
  if (props.visible) {
    // 只在首次绘制时输出调试信息
    if (animationState.value.firstRender) {
      animationState.value.firstRender = false
    }
    
    // 优化渲染频率：在缩放、拖拽或悬停时实时渲染
    const shouldRender = interactionState.value.isDragging || 
                        interactionState.value.isScaling ||   // 缩放时强制渲染
                        hoveredLine.value !== null ||
                        interactionState.value.hoveredStation !== null ||
                        Math.floor(time / 50) % 3 === 0 // 平时降低渲染频率
    
    if (!shouldRender && animationState.value.time > 500) {
      animationId.value = requestAnimationFrame(render)
      return
    }
    
    // 清空画布
    ctx.clearRect(0, 0, canvasWidth.value, canvasHeight.value)
    
    // 应用变换矩阵
    ctx.save()
    ctx.translate(interactionState.value.offsetX, interactionState.value.offsetY)
    ctx.scale(interactionState.value.scale, interactionState.value.scale)
    
    // 过滤可见的线路和站点
    const visibleLines = lines.value.filter(line => shouldShowLine(line.name))
    const visibleStations = stations.value.filter(station => shouldShowStation(station))
    
    // 绘制线路
    for (const line of visibleLines) {
      drawLine(ctx, line, time)
    }
    
    // 处理换乘站：按站名分组
    const stationGroups = new Map<string, Station[]>()
    for (const station of visibleStations) {
      if (!stationGroups.has(station.name)) {
        stationGroups.set(station.name, [])
      }
      stationGroups.get(station.name)!.push(station)
    }
    
    // 绘制所有站点圆圈
    for (const station of visibleStations) {
      drawStation(ctx, station, time)
    }
    
    // 绘制换乘站连接线和统一站名
    for (const [stationName, stationList] of stationGroups) {
      if (stationList.length > 1) {
        // 这是换乘站，绘制连接线
        drawTransferConnections(ctx, stationList)
        // 只绘制一个站名（选择第一个站点的位置）
        drawTransferStationName(ctx, stationList[0])
      } else {
        // 单线路站点，正常绘制站名
        drawStationName(ctx, stationList[0])
      }
    }
    
    // 恢复变换矩阵
    ctx.restore()
    
    // 绘制悬停信息
    if (interactionState.value.hoveredStation) {
      drawHoverInfo(ctx, interactionState.value.hoveredStation)
    }
  }
  
  animationId.value = requestAnimationFrame(render)
}

// 绘制悬停信息
const drawHoverInfo = (ctx: CanvasRenderingContext2D, station: Station) => {
  // 找到该站点的所有线路（换乘站情况）
  const stationLines = stations.value
    .filter(s => s.name === station.name)
    .map(s => s.line)
    .filter((line, index, self) => self.indexOf(line) === index) // 去重
    .sort((a, b) => {
      // 自定义排序：数字线路按数字排序，特殊线路排在最后
      const getLineNumber = (line: string) => {
        const match = line.match(/(\d+)号线/)
        return match ? parseInt(match[1]) : 999 // 特殊线路返回999，排在最后
      }
      
      const numA = getLineNumber(a)
      const numB = getLineNumber(b)
      
      if (numA !== numB) {
        return numA - numB
      }
      
      // 如果都是特殊线路，按字母顺序排序
      return a.localeCompare(b)
    })
  
  const padding = 12
  const infoWidth = 200
  const lineHeight = 18
  const baseHeight = 70 // 增加高度以容纳开通状态信息
  const infoHeight = baseHeight + (Math.max(0, stationLines.length - 1) * lineHeight)
  const x = canvasWidth.value - infoWidth - 20
  const y = canvasHeight.value - infoHeight - 20
  
  // 背景绘制（不使用闪烁动画）
  ctx.fillStyle = 'rgba(255, 255, 255, 0.95)'
  ctx.shadowColor = 'rgba(0, 0, 0, 0.2)'
  ctx.shadowBlur = 12
  ctx.shadowOffsetX = 0
  ctx.shadowOffsetY = 3
  
  // 简单矩形背景（添加圆角效果）
  ctx.beginPath()
  ctx.roundRect(x, y, infoWidth, infoHeight, 8)
  ctx.fill()
  
  // 重置阴影
  ctx.shadowColor = 'transparent'
  ctx.shadowBlur = 0
  ctx.shadowOffsetX = 0
  ctx.shadowOffsetY = 0
  
  // 获取优化后的线路颜色（用于白色背景显示）
  const getOptimizedLineColor = (line: string) => {
    const originalColor = lineColors[line] || '#64748b'
    
    // 对于在白色背景下显示效果不好的颜色进行优化
    switch (line) {
      case '市域机场线':
        return '#333333' // 深灰色替代白色
      case '磁浮线':
        return '#008B9A' // 使用青色部分
      case '3号线':
        return '#E6B800' // 稍微深一点的黄色
      case '9号线':
        return '#5BA3D4' // 稍微深一点的蓝色
      case '浦江线':
        return '#8A8A8B' // 稍微深一点的灰色
      default:
        return originalColor
    }
  }
  
  // 左侧多线路颜色条（带圆角）
  const colorBarWidth = 6
  if (stationLines.length === 1) {
    // 单线路：显示单一颜色条
    const lineColor = getOptimizedLineColor(stationLines[0])
    ctx.fillStyle = lineColor
    ctx.beginPath()
    ctx.roundRect(x, y, colorBarWidth, infoHeight, [8, 0, 0, 8])
    ctx.fill()
  } else {
    // 多线路：显示分段颜色条
    const segmentHeight = infoHeight / stationLines.length
    stationLines.forEach((line, index) => {
      const lineColor = getOptimizedLineColor(line)
      ctx.fillStyle = lineColor
      
      // 为第一个和最后一个段添加圆角
      if (index === 0) {
        ctx.beginPath()
        ctx.roundRect(x, y + (index * segmentHeight), colorBarWidth, segmentHeight, [8, 0, 0, 0])
        ctx.fill()
      } else if (index === stationLines.length - 1) {
        ctx.beginPath()
        ctx.roundRect(x, y + (index * segmentHeight), colorBarWidth, segmentHeight, [0, 0, 0, 8])
        ctx.fill()
      } else {
        ctx.fillRect(x, y + (index * segmentHeight), colorBarWidth, segmentHeight)
      }
    })
  }
  
  // 站名（主标题）- 固定透明度
  ctx.fillStyle = 'rgba(31, 41, 55, 1)'
  ctx.font = 'bold 16px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  ctx.textAlign = 'left'
  ctx.textBaseline = 'top'
  ctx.fillText(station.name, x + padding + 2, y + padding)
  
  // 开通状态显示
  ctx.font = '12px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  if (station.isOpened) {
    ctx.fillStyle = '#059669' // 绿色表示已开通
    ctx.fillText('已开通运营', x + padding + 2, y + padding + 22)
  } else {
    ctx.fillStyle = '#dc2626' // 红色表示未开通
    ctx.fillText('未开通运营', x + padding + 2, y + padding + 22)
  }
  
  // 线路信息（显示所有线路）- 固定透明度
  ctx.font = '14px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  stationLines.forEach((line, index) => {
    const lineColor = getOptimizedLineColor(line)
    ctx.fillStyle = lineColor
    ctx.fillText(line, x + padding + 2, y + padding + 42 + (index * lineHeight))
  })
}

// 重新计算坐标（窗口大小变化时使用）
const recalculateCoordinates = () => {
  if (stations.value.length === 0) return
  
  // 计算坐标边界
  let minLng = Infinity, maxLng = -Infinity
  let minLat = Infinity, maxLat = -Infinity
  
  for (const station of stations.value) {
    minLng = Math.min(minLng, station.lng)
    maxLng = Math.max(maxLng, station.lng)
    minLat = Math.min(minLat, station.lat)
    maxLat = Math.max(maxLat, station.lat)
  }
  
  const bounds = { minLng, maxLng, minLat, maxLat }
  
  // 重新计算屏幕坐标
  for (const station of stations.value) {
    const coords = convertCoordinates(station.lng, station.lat, bounds)
    station.x = coords.x
    station.y = coords.y
  }
  
  // 清除绘制缓存，强制重新绘制
  drawingCache.value.clear()
}

// 处理窗口大小变化（添加防抖）
let resizeTimeout: number
const handleResize = () => {
  clearTimeout(resizeTimeout)
  resizeTimeout = setTimeout(() => {
    canvasWidth.value = window.innerWidth
    canvasHeight.value = window.innerHeight
    
    if (stations.value.length > 0) {
      // 只重新计算坐标，不重新解析CSV
      nextTick(() => {
        recalculateCoordinates()
      })
    }
  }, 200)
}

onMounted(async () => {
  await parseCSVData()
  
  // 绑定全局事件监听器 - 只在可见时处理
  const conditionalMouseUp = (e: MouseEvent) => {
    if (props.visible) handleMouseUp(e)
  }
  
  const conditionalMouseMove = (e: MouseEvent) => {
    if (props.visible) handleMouseMove(e)
  }
  
  document.addEventListener('mouseup', conditionalMouseUp)
  document.addEventListener('mousemove', conditionalMouseMove)
  
  // 存储清理函数的引用
  ;(window as any).__metroNetworkCleanupFunctions = {
    mouseUp: conditionalMouseUp,
    mouseMove: conditionalMouseMove
  }
  
  // 等待DOM更新后开始动画
  await nextTick()
  animationId.value = requestAnimationFrame(render)
  
  window.addEventListener('resize', handleResize)
  
})

// 监听visible属性变化
watch(() => props.visible, (newValue) => {
  if (newValue && stations.value.length === 0) {
    parseCSVData()
  }
})

// 定位到指定站点的功能
const centerOnStation = (stationName: string) => {
  
  // 查找匹配的站点
  const targetStation = stations.value.find(station => 
    station.name.includes(stationName) || stationName.includes(station.name)
  )
  
  if (!targetStation) {
    return false
  }
  
  
  // 计算居中所需的偏移量
  const targetScale = 2.5 // 放大到更明显的比例
  const centerX = canvasWidth.value / 2
  const centerY = canvasHeight.value / 2
  
  // 计算新的偏移量，让站点居中
  const targetOffsetX = centerX - targetStation.x * targetScale
  const targetOffsetY = centerY - targetStation.y * targetScale
  
  // 使用GSAP进行平滑动画
  if (zoomAnimation.value && typeof zoomAnimation.value.kill === 'function') {
    zoomAnimation.value.kill()
  }
  
  // 清除之前的高亮状态和定时器
  if (highlightClearTimer.value) {
    clearTimeout(highlightClearTimer.value)
    highlightClearTimer.value = null
  }
  interactionState.value.highlightedSegment = null
  
  zoomAnimation.value = gsap.to(interactionState.value, {
    scale: targetScale,
    offsetX: targetOffsetX,
    offsetY: targetOffsetY,
    duration: 2.0, // 增加动画时长让效果更明显
    ease: "power2.inOut",
    onUpdate: () => {
      // 强制重新渲染
      interactionState.value.isScaling = true
    },
    onComplete: () => {
      interactionState.value.isScaling = false
      // 设置悬停状态，高亮目标站点
      interactionState.value.hoveredStation = targetStation
    }
  })
  
  return true
}

// 高亮线路段的功能
const highlightLineSegment = (fromStation: string, toStation: string, lineName: string) => {
  
  // 清除之前的动画和定时器，防止冲突
  if (zoomAnimation.value && typeof zoomAnimation.value.kill === 'function') {
    zoomAnimation.value.kill()
    zoomAnimation.value = null
  }
  
  if (highlightClearTimer.value) {
    clearTimeout(highlightClearTimer.value)
    highlightClearTimer.value = null
  }
  
  // 查找起点和终点站点
  const fromStationObj = stations.value.find(station => 
    (station.name.includes(fromStation) || fromStation.includes(station.name)) &&
    station.line === lineName
  )
  
  const toStationObj = stations.value.find(station => 
    (station.name.includes(toStation) || toStation.includes(station.name)) &&
    station.line === lineName
  )
  
  if (!fromStationObj || !toStationObj) {
    // 如果找不到线路段，则尝试跳转到起点站
    return centerOnStation(fromStation)
  }
  
  
  // 设置高亮线路段
  interactionState.value.highlightedSegment = {
    fromStation: fromStationObj.name,
    toStation: toStationObj.name,
    lineName: lineName
  }
  
  // 计算两个站点的中心点
  const centerX = (fromStationObj.x + toStationObj.x) / 2
  const centerY = (fromStationObj.y + toStationObj.y) / 2
  
  // 计算合适的缩放级别（根据两点距离调整）
  const distance = Math.sqrt(
    Math.pow(toStationObj.x - fromStationObj.x, 2) + 
    Math.pow(toStationObj.y - fromStationObj.y, 2)
  )
  
  // 根据距离自适应缩放级别
  const targetScale = Math.min(4.0, Math.max(2.0, 800 / distance))
  
  const canvasCenterX = canvasWidth.value / 2
  const canvasCenterY = canvasHeight.value / 2
  
  // 计算偏移量，让线路段中心居中
  const targetOffsetX = canvasCenterX - centerX * targetScale
  const targetOffsetY = canvasCenterY - centerY * targetScale
  
  // 使用GSAP进行平滑动画
  if (zoomAnimation.value) {
    zoomAnimation.value.kill()
  }
  
  zoomAnimation.value = gsap.to(interactionState.value, {
    scale: targetScale,
    offsetX: targetOffsetX,
    offsetY: targetOffsetY,
    duration: 3.0, // 更长的动画时间，让用户能清楚看到缩放过程
    ease: "power2.inOut",
    onUpdate: () => {
      interactionState.value.isScaling = true
    },
    onComplete: () => {
      interactionState.value.isScaling = false
      
      // 8秒后清除高亮，给用户更多时间观察
      highlightClearTimer.value = setTimeout(() => {
        interactionState.value.highlightedSegment = null
        highlightClearTimer.value = null
      }, 8000)
    }
  })
  
  return true
}

// 暴露方法给父组件
defineExpose({
  centerOnStation,
  highlightLineSegment
})

onUnmounted(() => {
  if (animationId.value) {
    cancelAnimationFrame(animationId.value)
  }
  
  // 清理GSAP动画
  if (zoomAnimation.value) {
    zoomAnimation.value.kill()
    zoomAnimation.value = null
  }
  
  // 清理高亮定时器
  if (highlightClearTimer.value) {
    clearTimeout(highlightClearTimer.value)
    highlightClearTimer.value = null
  }
  
  // 清理缓存
  drawingCache.value.clear()
  
  window.removeEventListener('resize', handleResize)
  
  // 清理全局事件监听器
  const cleanupFunctions = (window as any).__metroNetworkCleanupFunctions
  if (cleanupFunctions) {
    document.removeEventListener('mouseup', cleanupFunctions.mouseUp)
    document.removeEventListener('mousemove', cleanupFunctions.mouseMove)
    delete (window as any).__metroNetworkCleanupFunctions
  }
})
</script>

<style scoped>
.metro-network-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1; /* 默认在背景层 */
  pointer-events: none; /* 确保背景不阻挡交互 */
  background: transparent; /* 默认透明背景 */
  transition: background-color 0.5s ease, z-index 0s 0.5s; /* 背景色过渡，z-index延迟变化 */
}

.metro-network-background.visible {
  background: #000; /* 只有在可见时才显示黑色背景 */
  z-index: 10; /* 显示时提升层级 */
  transition: background-color 0.5s ease, z-index 0s 0s; /* 立即提升z-index */
  pointer-events: auto; /* 可见时允许交互 */
}

.metro-canvas {
  width: 100%;
  height: 100%;
  display: block;
  opacity: 0;
  background: transparent;
  transition: opacity 0.5s ease;
  cursor: grab;
}

.metro-canvas.visible {
  opacity: 1; /* 完全不透明 */
  pointer-events: auto; /* 可见时允许交互 */
}

/* 简化的测试按钮 */
.metro-toggle-btn-test {
  position: fixed !important;
  bottom: 20px !important;
  left: 20px !important;
  z-index: 999999 !important;
  pointer-events: auto !important;
}

.simple-btn {
  background: #ff0000 !important; /* 红色，非常明显 */
  color: white !important;
  border: 3px solid #ffffff !important;
  padding: 15px 20px !important;
  font-size: 16px !important;
  font-weight: bold !important;
  border-radius: 8px !important;
  cursor: pointer !important;
  box-shadow: 0 4px 8px rgba(0,0,0,0.3) !important;
  min-width: 150px !important;
  position: relative !important;
  z-index: 1000000 !important;
}

.simple-btn:hover {
  background: #cc0000 !important;
  transform: scale(1.05) !important;
}

/* 移动端优化 */
@media (max-width: 768px) {
  .metro-toggle-btn-test {
    bottom: 16px !important;
    left: 16px !important;
  }
  
  .simple-btn {
    padding: 12px 16px !important;
    font-size: 14px !important;
    min-width: 120px !important;
  }
}

/* 即时提示框样式 */
.button-tooltip {
  position: fixed;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  z-index: 10000;
  pointer-events: none;
  transform: translateX(-50%) translateY(-100%);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  animation: tooltipFadeIn 0.15s ease-out;
}

.button-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid rgba(0, 0, 0, 0.9);
}

@keyframes tooltipFadeIn {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-100%) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(-100%) scale(1);
  }
}

/* 上海地铁风格线路控制按钮样式 */
.metro-line-controls {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  pointer-events: auto;
  background: transparent;
  padding: 0;
  border-radius: 0;
  backdrop-filter: none;
  box-shadow: none;
  border: none;
  max-width: 600px;
  overflow: visible;
}

.metro-line-row {
  display: flex;
  gap: 4px;
  justify-content: center;
  align-items: center;
  flex-wrap: nowrap;
}

.shanghai-metro-line-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 26px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  overflow: hidden;
  width: 56px;
  position: relative;
  backdrop-filter: blur(10px);
}

.shanghai-metro-line-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  border-color: rgba(255, 255, 255, 0.5);
}

.shanghai-metro-line-btn.active {
  background: var(--line-color);
  border-color: var(--line-color);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
}

/* 市域机场线特殊样式：白色背景+黑色边框 */
.shanghai-metro-line-btn.active.line-市域机场 {
  background: #FFFFFF;
  border: 2px solid #000000;
  color: #000000;
}

/* 磁浮线特殊样式：对半分颜色 */
.shanghai-metro-line-btn.active.line-磁浮 {
  background: linear-gradient(to bottom, #008B9A 50%, #FF9147 50%);
  border-color: #008B9A;
  color: #FFFFFF;
}

.shanghai-metro-line-btn.inactive {
  background: rgba(255, 255, 255, 0.8);
  border-color: rgba(255, 255, 255, 0.4);
}

.shanghai-metro-line-btn.hovered {
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
  z-index: 100;
  border-color: var(--line-color);
}

.shanghai-metro-line-btn.faded {
  opacity: 0.3;
  transform: scale(0.98);
}

/* 特殊长线路名称的按钮样式 */
.shanghai-metro-line-btn.line-市域机场,
.shanghai-metro-line-btn.line-示范区 {
  width: 68px; /* 增加宽度以容纳更长的文字 */
}

/* 线路信息区域 */
.line-info {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2px 6px;
  min-width: 0;
  flex: 1;
}

.line-name-cn {
  font-size: 11px;
  font-weight: 600;
  line-height: 1.1;
  color: #1f2937;
  font-family: 'PingFang SC', 'Microsoft YaHei', '微软雅黑', sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.shanghai-metro-line-btn.active .line-name-cn {
  color: var(--text-color);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.line-name-en {
  font-size: 7px;
  font-weight: 500;
  line-height: 1.1;
  color: #6b7280;
  font-family: 'SF Pro Text', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 0.5px;
}

/* 特殊长线路名称的英文字体调整 */
.shanghai-metro-line-btn.line-市域机场 .line-name-en,
.shanghai-metro-line-btn.line-示范区 .line-name-en {
  font-size: 6px;
  letter-spacing: 0.1px;
}

.shanghai-metro-line-btn.active .line-name-en {
  color: var(--text-color);
  opacity: 0.8;
}

.shanghai-metro-line-btn.inactive .line-name-en {
  color: #9ca3af;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .metro-line-controls {
    bottom: 16px;
    max-width: 90vw;
  }
  
  .metro-line-row {
    gap: 3px;
  }
  
  .shanghai-metro-line-btn {
    height: 24px;
    width: 50px;
    border-radius: 5px;
  }
  
  /* 移动端特殊长线路名称按钮 */
  .shanghai-metro-line-btn.line-市域机场,
  .shanghai-metro-line-btn.line-示范区 {
    width: 60px;
    font-size: 6px;
  }
  
  .line-info {
    padding: 1px 4px;
  }
  
  .line-name-cn {
    font-size: 10px;
  }
  
  .line-name-en {
    font-size: 6px;
  }
}

/* 超小屏幕：进一步压缩 */
@media (max-width: 480px) {
  .metro-line-controls {
    max-width: 95vw;
    bottom: 12px;
  }
  
  .metro-line-row {
    gap: 2px;
    flex-wrap: wrap;
    justify-content: center;
  }
  
  .shanghai-metro-line-btn {
    width: 44px;
    height: 22px;
  }
  
  .line-name-cn {
    font-size: 9px;
  }
  
  .line-name-en {
    font-size: 5px;
  }
}
</style>
